/**
 * Issue #260 — 数据总览 KPI 下钻（在线员工 / 今日通话）客户端契约 + API + 纯展示助手。
 *
 * 与 apps/api/src/modules/admin/dto/overview-drilldown.dto.ts 契约对齐（apiClient 已解包
 * envelope.data）。客户名隐私沿用 #178：customerNamePlain 仅 ADMIN/SUPER_ADMIN 非 null，
 * 否则展示 customerNameMasked。在线时长为登录/登出审计推导值（近似），UI 以「约」标注。
 */
import { apiClient } from './api';
import type { QcResult } from './types';

export interface OnlineEmployeeItem {
  employeeId: string;
  name: string;
  deptName: string | null;
  jobTitle: string | null;
  lastLoginAt: string | null;
  onlineDurationSec: number;
}

export interface OnlineEmployeesView {
  items: OnlineEmployeeItem[];
  total: number;
  page: number;
  limit: number;
  onlineWindowMinutes: number;
  onlineDurationSource: 'login_logout_audit';
  generatedAt: string;
}

export interface TodayCallItem {
  callRecordId: string;
  employeeId: string;
  employeeName: string | null;
  deptName: string | null;
  customerNameMasked: string;
  customerNamePlain: string | null;
  startedAt: string;
  endedAt: string | null;
  durationSec: number;
  aiScore: number | null;
  qcScore: number | null;
  /** 后端已 normalizeQcResult → PASS/FAIL/PENDING 之一（或 null）。 */
  qcResult: QcResult | null;
  status: string;
}

export interface TodayCallsView {
  items: TodayCallItem[];
  total: number;
  page: number;
  limit: number;
  dayStart: string;
  generatedAt: string;
}

export const ONLINE_EMPLOYEES_PATH = '/admin/overview/online-employees';
export const TODAY_CALLS_PATH = '/admin/overview/today-calls';

export function getOnlineEmployeesApi(
  params: { page?: number; limit?: number } = {},
): Promise<OnlineEmployeesView> {
  const qs = encodeDrilldownQuery(params);
  return apiClient.get<OnlineEmployeesView>(`${ONLINE_EMPLOYEES_PATH}${qs}`);
}

export function getTodayCallsApi(
  params: { page?: number; limit?: number } = {},
): Promise<TodayCallsView> {
  const qs = encodeDrilldownQuery(params);
  return apiClient.get<TodayCallsView>(`${TODAY_CALLS_PATH}${qs}`);
}

/** 拼接分页 query（仅含已提供的 page/limit），保证 URL 稳定可断言。 */
export function encodeDrilldownQuery(params: { page?: number; limit?: number }): string {
  const parts: string[] = [];
  if (typeof params.page === 'number') parts.push(`page=${params.page}`);
  if (typeof params.limit === 'number') parts.push(`limit=${params.limit}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/** 在线时长（秒）→ 人类可读「约 Xh Ym」/「约 Y 分钟」/「< 1 分钟」。纯函数，便于单测。 */
export function formatOnlineDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '约 0 分钟';
  const totalMin = Math.floor(sec / 60);
  if (totalMin <= 0) return '约 < 1 分钟';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `约 ${h} 小时 ${m} 分钟`;
  return `约 ${m} 分钟`;
}

/** 客户名展示：有完整客户名（ADMIN/SUPER_ADMIN）优先，否则脱敏名，再否则匿名占位。 */
export function customerNameDisplay(item: {
  customerNamePlain: string | null;
  customerNameMasked: string;
}): string {
  if (item.customerNamePlain && item.customerNamePlain.length > 0) {
    return item.customerNamePlain;
  }
  if (item.customerNameMasked && item.customerNameMasked.length > 0) {
    return item.customerNameMasked;
  }
  return '客户';
}
